/**
 * Hour slots encoding utilities for admin client
 * Encodes/decodes 12-character strings representing availability from 7am-6pm
 * 
 * Format: Each character represents one hour
 * N = Not available
 * V = Available for virtual jobs only
 * P = Available for in-person jobs only  
 * B = Available for both virtual and in-person jobs
 * 
 * Position 0 = 7am, Position 1 = 8am, ..., Position 11 = 6pm
 */

export type JobType = 'virtual' | 'in-person';

export class HourSlotsEncoder {
  /**
   * Convert hour slots object to 12-character string
   */
  static encode(hourSlots: Record<number, JobType[]>): string {
    let result = 'NNNNNNNNNNNN';
    
    for (let hour = 7; hour <= 18; hour++) { // 7am to 6pm
      const slotIndex = hour - 7; // 0-11
      const jobTypes = hourSlots[hour] || [];
      
      let char = 'N';
      if (jobTypes.includes('virtual') && jobTypes.includes('in-person')) {
        char = 'B'; // Both
      } else if (jobTypes.includes('in-person')) {
        char = 'P'; // In-person only
      } else if (jobTypes.includes('virtual')) {
        char = 'V'; // Virtual only
      }
      
      result = result.substring(0, slotIndex) + char + result.substring(slotIndex + 1);
    }
    
    return result;
  }
  
  /**
   * Convert 12-character string to hour slots object
   */
  static decode(hourSlots: string): Record<number, JobType[]> {
    const result: Record<number, JobType[]> = {};
    
    for (let i = 0; i < Math.min(12, hourSlots.length); i++) {
      const hour = i + 7; // 7am to 6pm
      const char = hourSlots[i];
      
      switch (char) {
        case 'V':
          result[hour] = ['virtual'];
          break;
        case 'P':
          result[hour] = ['in-person'];
          break;
        case 'B':
          result[hour] = ['virtual', 'in-person'];
          break;
        case 'N':
        default:
          // Not available - don't add to result
          break;
      }
    }
    
    return result;
  }
  
  /**
   * Get combined job types from hour slots string
   */
  static getJobTypes(hourSlots: string): string[] {
    const jobTypesSet = new Set<string>();
    
    for (const char of hourSlots) {
      switch (char) {
        case 'V':
          jobTypesSet.add('virtual');
          break;
        case 'P':
          jobTypesSet.add('in-person');
          break;
        case 'B':
          jobTypesSet.add('virtual');
          jobTypesSet.add('in-person');
          break;
      }
    }
    
    return Array.from(jobTypesSet);
  }
  
  /**
   * Get start and end times from hour slots string
   */
  static getTimeRange(hourSlots: string): { startTime?: string; endTime?: string } {
    let firstAvailableHour = -1;
    let lastAvailableHour = -1;
    
    for (let i = 0; i < Math.min(12, hourSlots.length); i++) {
      if (hourSlots[i] !== 'N') {
        if (firstAvailableHour === -1) {
          firstAvailableHour = i;
        }
        lastAvailableHour = i;
      }
    }
    
    if (firstAvailableHour === -1) {
      return {}; // No availability
    }
    
    const startHour = firstAvailableHour + 7;
    const endHour = lastAvailableHour + 8; // End time is one hour after the last slot
    
    return {
      startTime: `${startHour.toString().padStart(2, '0')}:00`,
      endTime: `${endHour.toString().padStart(2, '0')}:00`
    };
  }

  /**
   * Check if hour slots string represents any availability
   */
  static hasAvailability(hourSlots: string): boolean {
    return hourSlots.includes('V') || hourSlots.includes('P') || hourSlots.includes('B');
  }
  
  /**
   * Get contiguous time blocks with job types
   */
  static getTimeBlocks(hourSlots: string): Array<{ startTime: string; endTime: string; jobTypes: JobType[] }> {
    const blocks: Array<{ startTime: string; endTime: string; jobTypes: JobType[] }> = [];
    
    let currentBlockStart = -1;
    let currentBlockTypes: string | null = null;
    
    for (let i = 0; i < Math.min(12, hourSlots.length); i++) {
      const char = hourSlots[i];
      
      if (char === 'N') {
        // End current block if exists
        if (currentBlockStart !== -1 && currentBlockTypes !== null) {
          const jobTypes = this.charToJobTypes(currentBlockTypes);
          blocks.push({
            startTime: `${(currentBlockStart + 7).toString().padStart(2, '0')}:00`,
            endTime: `${(i + 7).toString().padStart(2, '0')}:00`,
            jobTypes
          });
        }
        currentBlockStart = -1;
        currentBlockTypes = null;
      } else {
        // Check if this is a new block or continuation
        if (currentBlockTypes !== char) {
          // End previous block if exists
          if (currentBlockStart !== -1 && currentBlockTypes !== null) {
            const jobTypes = this.charToJobTypes(currentBlockTypes);
            blocks.push({
              startTime: `${(currentBlockStart + 7).toString().padStart(2, '0')}:00`,
              endTime: `${(i + 7).toString().padStart(2, '0')}:00`,
              jobTypes
            });
          }
          // Start new block
          currentBlockStart = i;
          currentBlockTypes = char;
        }
      }
    }
    
    // Handle last block if exists
    if (currentBlockStart !== -1 && currentBlockTypes !== null) {
      const jobTypes = this.charToJobTypes(currentBlockTypes);
      blocks.push({
        startTime: `${(currentBlockStart + 7).toString().padStart(2, '0')}:00`,
        endTime: `${(12 + 7).toString().padStart(2, '0')}:00`, // 7pm (end of day)
        jobTypes
      });
    }
    
    return blocks;
  }
  
  /**
   * Convert character to job types array
   */
  private static charToJobTypes(char: string): JobType[] {
    switch (char) {
      case 'V':
        return ['virtual'];
      case 'P':
        return ['in-person'];
      case 'B':
        return ['virtual', 'in-person'];
      default:
        return [];
    }
  }
  
  /**
   * Format time blocks as a readable string
   */
  static formatTimeBlocks(blocks: Array<{ startTime: string; endTime: string; jobTypes: JobType[] }>): string {
    if (blocks.length === 0) return '';
    
    return blocks.map(block => {
      const startTime = this.formatTime(block.startTime);
      const endTime = this.formatTime(block.endTime);
      
      let jobTypeIndicator = '';
      const hasVirtual = block.jobTypes.includes('virtual');
      const hasInPerson = block.jobTypes.includes('in-person');
      
      if (hasVirtual && hasInPerson) {
        jobTypeIndicator = ''; // ' V-IP' can be omitted for brevity
      } else if (hasVirtual) {
        jobTypeIndicator = ' V';
      } else if (hasInPerson) {
        jobTypeIndicator = ' IP';
      }
      
      return `${startTime}-${endTime}${jobTypeIndicator}`;
    }).join(', ');
  }
  
  /**
   * Format time to 12-hour format
   */
  private static formatTime(timeStr: string): string {
    if (!timeStr) return '';
    
    const [hours] = timeStr.split(':');
    const hour = parseInt(hours, 10);
    
    // Convert to 12-hour format without AM/PM (context is clear)
    if (hour === 0) return '12'; // midnight
    else if (hour > 12) return (hour - 12).toString(); // PM hours
    else return hour.toString();
  }
  
  /**
   * Apply state preference mask to hour slots
   */
  static applyStatePreferenceMask(
    hourSlots: string,
    virtualAllowed: boolean,
    inPersonAllowed: boolean
  ): string {
    if (virtualAllowed && inPersonAllowed) {
      // Both allowed - no masking needed
      return hourSlots;
    }
    
    if (!virtualAllowed && !inPersonAllowed) {
      // Neither allowed - all unavailable
      return 'NNNNNNNNNNNN';
    }
    
    // Apply mask character by character
    let maskedSlots = '';
    for (let i = 0; i < hourSlots.length; i++) {
      const char = hourSlots[i];
      
      if (char === 'N') {
        maskedSlots += 'N';
      } else if (char === 'V') {
        // Virtual only slot
        maskedSlots += virtualAllowed ? 'V' : 'N';
      } else if (char === 'P') {
        // In-person only slot
        maskedSlots += inPersonAllowed ? 'P' : 'N';
      } else if (char === 'B') {
        // Both types slot - adjust based on what's allowed
        if (virtualAllowed && inPersonAllowed) {
          maskedSlots += 'B';
        } else if (virtualAllowed) {
          maskedSlots += 'V';
        } else if (inPersonAllowed) {
          maskedSlots += 'P';
        } else {
          maskedSlots += 'N';
        }
      } else {
        // Unknown character - treat as unavailable
        maskedSlots += 'N';
      }
    }
    
    return maskedSlots;
  }
  
  /**
   * Apply state preference mask to time blocks
   */
  static applyStatePreferenceMaskToBlocks(
    timeBlocks: Array<{ startTime: string; endTime: string; jobTypes: JobType[] }>,
    virtualAllowed: boolean,
    inPersonAllowed: boolean
  ): Array<{ startTime: string; endTime: string; jobTypes: JobType[] }> {
    if (virtualAllowed && inPersonAllowed) {
      // Both allowed - no masking needed
      return timeBlocks;
    }
    
    if (!virtualAllowed && !inPersonAllowed) {
      // Neither allowed - return empty array
      return [];
    }
    
    // Filter job types in each block based on what's allowed
    const maskedBlocks: Array<{ startTime: string; endTime: string; jobTypes: JobType[] }> = [];
    
    for (const block of timeBlocks) {
      const filteredJobTypes = block.jobTypes.filter(jobType => {
        if (jobType === 'virtual') return virtualAllowed;
        if (jobType === 'in-person') return inPersonAllowed;
        return false;
      });
      
      // Only include block if it has allowed job types
      if (filteredJobTypes.length > 0) {
        maskedBlocks.push({
          startTime: block.startTime,
          endTime: block.endTime,
          jobTypes: filteredJobTypes
        });
      }
    }
    
    // Merge contiguous blocks with same job types
    const mergedBlocks: Array<{ startTime: string; endTime: string; jobTypes: JobType[] }> = [];
    
    for (const block of maskedBlocks) {
      const lastBlock = mergedBlocks[mergedBlocks.length - 1];
      
      if (lastBlock && 
          lastBlock.endTime === block.startTime && 
          JSON.stringify(lastBlock.jobTypes.sort()) === JSON.stringify(block.jobTypes.sort())) {
        // Merge with previous block
        lastBlock.endTime = block.endTime;
      } else {
        // Add as new block
        mergedBlocks.push(block);
      }
    }
    
    return mergedBlocks;
  }
}